import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { getCountries } from '../../../api/countries/get-countries'
import { getFormulas } from '../../../api/formula/get-formulas'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  SchoolLevelEnum,
  schoolLevelEnumMapping
} from '../../../lib/types/enums/common/school-level.enum'
import {
  AccomodationCategoryEnum,
  accomodationCategoryEnumMapping
} from '../../../lib/types/enums/product/accomodation-category.enum'
import {
  ProductTypeEnum,
  productTypeOptions
} from '../../../lib/types/enums/product/product-type.enum'
import { ProductInput } from '../../../lib/types/models/product/product-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { LocaleKeys } from '../../../locale/translation-types'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ControlledTextEditor from '../../elements/inputs/controlled-text-editor'
import { ControlledListRadioGroup } from '../../elements/inputs/enum-radio-group'
import ValidationButtons from '../../elements/validation-buttons'
import { productFormSchema, requiresNumberOfHoursPerWeek } from './utils'

type Props = BaseFormProps<ProductInput> & {
  canUpdateAccomodation?: boolean
  canUpdateType?: boolean
}

const ProductForm = ({
  defaultValues,
  canUpdateAccomodation = true,
  canUpdateType = true,
  ...validationButtonProps
}: Props) => {
  const { t } = useTranslate()

  const form = useForm<ProductInput>({
    resolver: zodResolver(productFormSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: formulas } = useQuery({
    queryKey: ['formulas'],
    queryFn: () => getFormulas()
  })

  const countryId = form.watch('countryId')
  const formulaId = form.watch('formulaId')
  const isOtoIndividual = form.watch('type') === ProductTypeEnum.ONE_TO_ONE_INDIVIDUAL

  const name = useMemo(() => {
    if (countryId && formulaId && formulas && countries) {
      const formula = formulas?.data.find((formula) => formula.id === formulaId)
      const country = countries?.data.find((country) => country.id === countryId)
      return `${country?.name} - ${getTranslatedName(formula)}`
    }

    return ''
  }, [countryId, formulaId, countries, formulas])

  useEffect(() => {
    if (name && (!defaultValues || !defaultValues.name)) {
      form.setValue('name', name, { shouldValidate: true })
    }
  }, [name])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <InnerBox className={'p-4 rounded-2xl flex flex-col gap-3 '}>
          <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
            Produit
          </Typography>

          <ControlledListRadioGroup<ProductInput, ProductTypeEnum>
            required
            name="type"
            hideLabel
            list={Object.values(ProductTypeEnum)}
            mapping={productTypeOptions}
            disabledRadioInputs={canUpdateType ? undefined : Object.values(ProductTypeEnum)}
            valueFormatter={(value) => t(value as LocaleKeys)}
          />

          <div className={'flex gap-4 flex-row'}>
            <FormField
              control={form.control}
              name={'countryId'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      required
                      searchable
                      {...field}
                      label={'Pays'}
                      disabled={!!defaultValues?.countryId}
                      options={countries?.data.map((country) => ({
                        label: country.name,
                        value: country.id
                      }))}
                      selectedOption={conditionalRender(!!field?.value, {
                        value: field.value,
                        label:
                          countries?.data.find((country) => country.id === field.value)?.name ?? ''
                      })}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'formulaId'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      searchable
                      required
                      {...field}
                      label={'Formule'}
                      disabled={!!defaultValues?.formulaId}
                      options={formulas?.data.map((formula) => ({
                        label: getTranslatedName(formula),
                        value: formula.id
                      }))}
                      selectedOption={conditionalRender(!!field?.value, {
                        value: field.value,
                        label: getTranslatedName(
                          formulas?.data.find((country) => country.id === field.value)
                        )
                      })}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'name'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput required {...field} type="text" label="Nom du produit" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className={'flex gap-4 flex-row'}>
            <FormField
              control={form.control}
              name={'accomodationCategory'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      required
                      {...field}
                      disabled={!canUpdateAccomodation}
                      label={"Catégorie d'hébergement"}
                      options={Object.values(AccomodationCategoryEnum).map(
                        (accomodationCategory) => ({
                          label: accomodationCategoryEnumMapping[accomodationCategory],
                          value: accomodationCategory
                        })
                      )}
                      selectedOption={getSelectedEnumOption(
                        field.value,
                        accomodationCategoryEnumMapping
                      )}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'minAge'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput {...field} type="number" label="Âge min" />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'maxAge'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput {...field} type="number" label="Âge max" />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'splitAge'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput {...field} type="number" label="Âge split" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className={'flex gap-4 flex-row'}>
            <FormField
              control={form.control}
              name={'minSchoolLevel'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      required
                      searchable
                      {...field}
                      label={'Classe min'}
                      options={Object.values(SchoolLevelEnum).map((level) => ({
                        label: schoolLevelEnumMapping[level],
                        value: level
                      }))}
                      selectedOption={conditionalRender(!!field?.value, {
                        value: field.value,
                        label: schoolLevelEnumMapping[field.value]
                      })}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'maxSchoolLevel'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      searchable
                      {...field}
                      optional
                      label={'Classe max'}
                      options={Object.values(SchoolLevelEnum).map((level) => ({
                        label: schoolLevelEnumMapping[level],
                        value: level
                      }))}
                      selectedOption={conditionalRender(!!field?.value, {
                        value: field.value,
                        label: schoolLevelEnumMapping[field.value]
                      })}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className={'flex gap-4 flex-row'}>
            <FormField
              control={form.control}
              name={'minNumberOfStudents'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput {...field} type="number" label="# d'élèves min" />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'maxNumberOfStudents'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput {...field} type="number" label="# d'élèves max" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className={'flex gap-4 flex-row'}>
            <FormField
              control={form.control}
              name={'numberOfHoursPerDay'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput {...field} type="number" label="# d'h de cours / j" />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'numberOfHoursPerWeek'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput
                      required={requiresNumberOfHoursPerWeek(form.watch('type'))}
                      {...field}
                      type="number"
                      label="# d'h de cours / sem"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          {/* the TEFL / Full Day / Teaching Room fields do not apply to One to One Individuel */}
          {!isOtoIndividual && (
            <>
              <div className={'flex gap-4 flex-row'}>
                <FormField
                  control={form.control}
                  name={'numberOfTeflWithTuitionAndActivity'}
                  render={({ field }) => (
                    <FormItem className={'flex-1'}>
                      <FormControl>
                        <ControlledInput
                          {...field}
                          type="number"
                          label="# TEFL - Tuition and activity"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={'numberOfTeflWithTuitionOnly'}
                  render={({ field }) => (
                    <FormItem className={'flex-1'}>
                      <FormControl>
                        <ControlledInput {...field} type="number" label="# TEFL - Tuition only" />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div className={'flex gap-4 flex-row'}>
                <FormField
                  control={form.control}
                  name={'numberOfTeflWithActivityOnly'}
                  render={({ field }) => (
                    <FormItem className={'flex-1'}>
                      <FormControl>
                        <ControlledInput {...field} type="number" label="# TEFL - Activity only" />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={'numberOfTeflPerDay'}
                  render={({ field }) => (
                    <FormItem className={'flex-1'}>
                      <FormControl>
                        <ControlledInput
                          {...field}
                          type="number"
                          label="# TEFL - Full Day Outing"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={'numberOfFullDay'}
                  render={({ field }) => (
                    <FormItem className={'flex-1'}>
                      <FormControl>
                        <ControlledInput {...field} type="number" label="# Full Day Outing" />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={'numberOfTeachingRoom'}
                  render={({ field }) => (
                    <FormItem className={'flex-1'}>
                      <FormControl>
                        <ControlledInput {...field} type="number" label="# Teaching Room" />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </>
          )}
          <FormField
            control={form.control}
            name={'noTransportGuide'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledTextEditor
                    {...field}
                    placeholder={
                      'Informations d’arrivée et de départ sur le lieu de séjour en cas de “sans transport” (ce texte se reportera dans l’Espace Client)'
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </InnerBox>

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default ProductForm
